import { Injectable } from '@angular/core';
import { CachedResource } from './cached-resource';

/**
 * Root-level singleton registry of CachedResource instances, shared across
 * SideNavComponent (eager, AppModule) and DashboardComponent/NewDashboardComponent
 * (lazy DashboardModule) so identical API calls (e.g. mySettings, dashboardAttendanceNew)
 * collapse into one cached resource instead of one-per-component.
 *
 * Cache keys are namespaced per session+branch so a logout or branch switch
 * can never leak a previous account/branch's cached data (e.g. sidebar nav).
 */
@Injectable({ providedIn: 'root' })
export class ResourceCacheService {
  private readonly resources = new Map<string, CachedResource<any>>();

  get<T>(key: string, persist: boolean = true): CachedResource<T> {
    const namespacedKey = this.namespacedKey(key);
    let resource = this.resources.get(namespacedKey);
    if (!resource) {
      resource = new CachedResource<T>(namespacedKey, persist);
      this.resources.set(namespacedKey, resource);
    }
    return resource;
  }

  /** Wipes every cached resource this service knows about, and their localStorage entries. */
  clearAll(): void {
    this.resources.forEach(resource => resource.clear());
    this.resources.clear();
    try {
      Object.keys(localStorage)
        .filter(k => k.indexOf('dashCache_') === 0)
        .forEach(k => localStorage.removeItem(k));
    } catch { }
  }

  private namespacedKey(key: string): string {
    return `dashCache_${this.sessionScope()}_${key}`;
  }

  private sessionScope(): string {
    try {
      const token = localStorage.getItem('token') || 'anon';
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const branchId = userDetails?.defaultSetting?.defaultBranchId || 'noBranch';
      return `${token.slice(0, 16)}_${branchId}`;
    } catch {
      return 'default';
    }
  }
}
